import WrenchIcon from "@heroicons/react/20/solid/WrenchIcon";

import PrimaryButton from "components/button/primaryButton/PrimaryButton";
import { FormDocument } from "prismicio-types";
import { useGetDisplayMode } from "queries/legacy/admin-queries";
import { useGetProduct, useGetSchema } from "queries/legacy/product-queries";
import type { Page } from "types/queries";
import { SSR_CONFIGURATION_ERROR } from "utils/error-utils";
import { CANVAS_FALLBACK_CONTENT } from "utils/fallback-content";
import { useInterpolate } from "utils/prismic-utils";

import PageImage from "../../pageImage/PageImage";
import { PageLayout } from "../pages/Pages";

import styles from "./Page.module.scss";

type PageProps = {
  layout: PageLayout;
  onEditButtonClicked: (form: FormDocument) => void;
  page: Page;
  pageNumber: number;
};

export default function Page({
  layout,
  onEditButtonClicked,
  page,
  pageNumber,
}: PageProps) {
  const interpolate = useInterpolate();

  const { data: displayMode } = useGetDisplayMode();
  const { data: product } = useGetProduct();
  const { data: schema } = useGetSchema();

  if (!product) {
    throw SSR_CONFIGURATION_ERROR;
  }

  if (!schema) {
    return null;
  }

  const inPageEdit = product.content.product.in_page_edits.find(
    (edit) => edit.muse_page_id === page.info?.pageName
  );

  const form = inPageEdit && ((inPageEdit as any).form as FormDocument);

  if (form && !form.data) {
    throw new Error(
      `Invalid configuration for in-page edit "${
        page.info?.pageName ?? pageNumber
      }" - missing "form"`
    );
  }

  return (
    <li className={[styles.container, styles[layout]].join(" ")}>
      {/*
        Anchor scrolled to when drawer is clicked.
        Sits 15vh above the page to position nicely on scroll.
      */}
      <span
        aria-hidden
        className={styles.pageAnchor}
        id={page.info?.pageName}
      />

      {displayMode === "diagnostic" && (
        <div className={styles.diagnostic}>
          <WrenchIcon className={styles.diagnosticIcon} />

          <code className={styles.diagnosticBlock}>
            <p className={styles.diagnosticText}>
              muse_page_id:{" "}
              {page.info?.pageName ? (
                <span className={styles.pageName}>{page.info?.pageName}</span>
              ) : (
                <span className={styles.pageNameUndefined}>undefined</span>
              )}
            </p>
          </code>
        </div>
      )}

      <PageImage
        loading={pageNumber < 2 ? "eager" : "lazy"}
        page={page}
        pageNumber={pageNumber}
      />

      {form && (
        <div
          className={styles.editButtonWrapper}
          data-testid={`edit-page_${page.info?.pageName}`}
          id={`edit-page_${page.info?.pageName}`}
        >
          <PrimaryButton
            as="button"
            className={styles.editButton}
            onClick={() => onEditButtonClicked(form)}
          >
            {interpolate(
              inPageEdit.edit_button_label ??
                product.content.canvas.default_edit_page_label ??
                CANVAS_FALLBACK_CONTENT.default_edit_page_label
            )}
          </PrimaryButton>
        </div>
      )}
    </li>
  );
}
